import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, CheckCircle2, AlertTriangle, Download, FileText, Info } from "lucide-react";
import { fmtINR } from "@/lib/format";

/**
 * PartnersTab
 * ────────────
 * Partnership / LLP — statutory partner-level information required for
 * ITR-5 (firm) and each partner's personal ITR (u/s 40(b), 10(2A), 28(v)).
 */
export default function PartnersTab({ entityType }) {
  const [rows, setRows] = useState([]);
  const [totalShare, setTotalShare] = useState(0);
  const [balanced, setBalanced] = useState(true);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [newP, setNewP] = useState({
    name: "", pan: "", address: "", din: "", is_designated_partner: false,
    profit_share_pct: 0, interest_rate_pct: 12, remuneration_slab: "working",
    opening_capital: 0, contributions_during_year: 0, withdrawals_during_year: 0,
    interest_on_capital_paid: 0, remuneration_paid: 0, share_of_profit_credited: 0,
  });
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [bookProfit, setBookProfit] = useState(0);
  const [complianceResult, setComplianceResult] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [jvOpen, setJvOpen] = useState(false);
  const [jvBusy, setJvBusy] = useState(false);
  const [jvProfit, setJvProfit] = useState(0);
  const [jvPreview, setJvPreview] = useState(null);
  const [jvOverrides, setJvOverrides] = useState({});
  const [jvDate, setJvDate] = useState("");
  const [jvConfirm, setJvConfirm] = useState(false);
  const [jvHistory, setJvHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isLLP = entityType === "llp";
  const isSupportedEntity = entityType === "partnership" || entityType === "llp";

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, c, h] = await Promise.all([
        api.get("/firm-partners"),
        api.get("/firm-partners/config"),
        api.get("/firm-partners/profit-jv/history").catch(() => ({ data: { items: [] } })),
      ]);
      setRows(p.data.items || []);
      setTotalShare(p.data.total_profit_share_pct || 0);
      setBalanced(!!p.data.balanced);
      setConfig(c.data);
      setJvHistory(h.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load partners");
    }
    setBusy(false);
  }, []);

  useEffect(() => { if (isSupportedEntity) { load(); } }, [isSupportedEntity, load]);

  if (!isSupportedEntity) {
    return (
      <Card className="p-5 rounded-sm border">
        <div className="flex items-start gap-3 text-sm">
          <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            The <b>Partners</b> tab applies only to entity types <b>Partnership</b>
            {" "}and <b>LLP</b>. Current entity type: <code>{entityType}</code>.
            Change it on the <b>Company</b> tab if this is a Partnership / LLP firm.
          </div>
        </div>
      </Card>
    );
  }

  const create = async () => {
    if (!newP.name || newP.pan.length !== 10) {
      return toast.error("Name and 10-char PAN are required");
    }
    try {
      await api.post("/firm-partners", newP);
      toast.success("Partner added");
      setOpenNew(false);
      setNewP({ ...newP, name: "", pan: "", address: "", din: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this partner (soft delete)?")) return;
    try {
      await api.delete(`/firm-partners/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const saveConfig = async () => {
    try {
      await api.patch("/firm-partners/config", config);
      toast.success("Deed limits saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const runCompliance = async () => {
    if (bookProfit === 0) {
      return toast.warning("Enter FY book profit for the 40(b) check");
    }
    try {
      const r = await api.get("/firm-partners/compliance-check",
                                 { params: { book_profit: bookProfit } });
      setComplianceResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compliance check failed"); }
  };

  const openSchedule = async () => {
    try {
      const r = await api.get("/firm-partners/schedule-partners");
      setSchedule(r.data);
      setScheduleOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Schedule fetch failed"); }
  };

  const downloadCSV = () => {
    if (!schedule?.rows?.length) return;
    const cols = ["sr","name","pan","address","profit_share_pct","opening_capital",
                    "contributions","withdrawals","interest_on_capital","remuneration",
                    "share_of_profit","closing_capital"];
    const csv = [cols.join(",")].concat(
      schedule.rows.map(r => cols.map(c => `"${String(r[c] ?? "").replace(/"/g,'""')}"`).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ITR5_Schedule_Partners_${schedule.fy}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4" data-testid="partners-tab">
      {/* Header + actions */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">
            {isLLP ? "LLP · Designated + General Partners" : "Partnership Firm · Partners"}
          </div>
          <div className="text-sm text-muted-foreground mt-1 max-w-prose">
            Statutory partner-level information — feeds <b>ITR-5</b> Schedule-Partners
            and each partner's personal ITR (Sec 40(b) allowance, Sec 10(2A) exempt
            share of profit, Sec 28(v) taxability of interest & remuneration).
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setComplianceOpen(true)}
                    className="rounded-sm gap-1.5" data-testid="partners-compliance-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> 40(b) Compliance
          </Button>
          <Button variant="outline" size="sm" onClick={openSchedule}
                    className="rounded-sm gap-1.5" data-testid="partners-schedule-btn">
            <FileText className="w-3.5 h-3.5" /> ITR-5 Schedule
          </Button>
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}
                    className="rounded-sm gap-1.5" data-testid="partners-jv-history-btn">
            <FileText className="w-3.5 h-3.5" /> JV History{jvHistory.length ? ` (${jvHistory.length})` : ""}
          </Button>
          <Button variant="outline" size="sm"
                    onClick={() => { setJvOpen(true); setJvPreview(null); setJvOverrides({}); setJvConfirm(false); }}
                    className="rounded-sm gap-1.5 border-blue-500 text-blue-700 hover:bg-blue-50"
                    data-testid="partners-profit-jv-btn">
            <FileText className="w-3.5 h-3.5" /> Post Profit JV
          </Button>
          <Button size="sm" onClick={() => setOpenNew(true)}
                    className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5"
                    data-testid="partners-new-btn">
            <Plus className="w-3.5 h-3.5" /> Add Partner
          </Button>
        </div>
      </div>

      {/* Balance banner */}
      <Card className={`p-3 rounded-sm border-l-4 ${balanced ? "border-emerald-500 bg-emerald-500/5" : "border-amber-500 bg-amber-500/5"}`}>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>
              {rows.length} partner{rows.length !== 1 ? "s" : ""} on record ·{" "}
              Total profit share: <b>{totalShare.toFixed(2)}%</b>
            </span>
          </div>
          {!balanced && (
            <span className="text-amber-700 flex items-center gap-1 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" /> Total should equal 100%
            </span>
          )}
        </div>
      </Card>

      {/* Partners table */}
      <Card className="rounded-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">PAN</th>
                <th className="text-left p-2">Type</th>
                <th className="text-right p-2">Share %</th>
                <th className="text-right p-2">Int Rate</th>
                <th className="text-right p-2">Opening ₹</th>
                <th className="text-right p-2">Contrib. ₹</th>
                <th className="text-right p-2">Withdr. ₹</th>
                <th className="text-right p-2">Interest ₹</th>
                <th className="text-right p-2">Remun. ₹</th>
                <th className="text-right p-2">Profit ₹</th>
                <th className="text-right p-2">Closing ₹</th>
                <th className="text-center p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={13} className="text-center py-8 text-muted-foreground italic">
                  No partners on record yet. Click <b>Add Partner</b> above.
                </td></tr>
              )}
              {rows.map((p) => (
                <tr key={p.id} className="border-t hover:bg-muted/20" data-testid={`partner-row-${p.pan}`}>
                  <td className="p-2 font-medium">{p.name}</td>
                  <td className="p-2 font-mono">{p.pan}</td>
                  <td className="p-2 text-[10px] font-mono uppercase">
                    {isLLP
                      ? (p.is_designated_partner ? "DESIGNATED" : "PARTNER")
                      : (p.remuneration_slab === "working" ? "WORKING" : "SLEEPING")}
                  </td>
                  <td className="p-2 text-right tabular-nums">{Number(p.profit_share_pct || 0).toFixed(2)}%</td>
                  <td className="p-2 text-right tabular-nums">{Number(p.interest_rate_pct || 0).toFixed(1)}%</td>
                  <td className="p-2 text-right tabular-nums">{fmtINR(p.opening_capital)}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-700">{fmtINR(p.contributions_during_year)}</td>
                  <td className="p-2 text-right tabular-nums text-red-600">{fmtINR(p.withdrawals_during_year)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtINR(p.interest_on_capital_paid)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtINR(p.remuneration_paid)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtINR(p.share_of_profit_credited)}</td>
                  <td className="p-2 text-right tabular-nums font-medium">{fmtINR(p.closing_capital)}</td>
                  <td className="p-2 text-center">
                    <Button size="icon" variant="ghost" onClick={() => remove(p.id)}
                             className="h-7 w-7" data-testid={`partner-del-${p.pan}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Deed config */}
      {config && (
        <Card className="p-4 rounded-sm border">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">
            Deed limits · Sec 40(b) allowance
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Deed date</Label>
              <Input type="date" value={config.deed_date || ""}
                       onChange={(e) => setConfig({ ...config, deed_date: e.target.value })}
                       className="mt-1 rounded-sm h-9" data-testid="partners-deed-date" />
            </div>
            <div>
              <Label className="text-xs">Max interest rate (40(b)(iv))</Label>
              <Input type="number" step="0.5" value={config.max_interest_rate_pct}
                       onChange={(e) => setConfig({ ...config, max_interest_rate_pct: Number(e.target.value) })}
                       className="mt-1 rounded-sm h-9 font-mono" data-testid="partners-max-int" />
            </div>
            <div>
              <Label className="text-xs">Remuneration first-slab (₹)</Label>
              <Input type="number" value={config.remuneration_book_profit_first_slab}
                       onChange={(e) => setConfig({ ...config, remuneration_book_profit_first_slab: Number(e.target.value) })}
                       className="mt-1 rounded-sm h-9 font-mono" />
            </div>
            <div>
              <Label className="text-xs">First-slab remuneration %</Label>
              <Input type="number" step="1" value={config.remuneration_pct_first_slab}
                       onChange={(e) => setConfig({ ...config, remuneration_pct_first_slab: Number(e.target.value) })}
                       className="mt-1 rounded-sm h-9 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Second-slab remuneration %</Label>
              <Input type="number" step="1" value={config.remuneration_pct_second_slab}
                       onChange={(e) => setConfig({ ...config, remuneration_pct_second_slab: Number(e.target.value) })}
                       className="mt-1 rounded-sm h-9 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Min remuneration (₹)</Label>
              <Input type="number" value={config.remuneration_fixed_min}
                       onChange={(e) => setConfig({ ...config, remuneration_fixed_min: Number(e.target.value) })}
                       className="mt-1 rounded-sm h-9 font-mono" />
            </div>
          </div>
          <Button onClick={saveConfig} size="sm"
                    className="mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white"
                    data-testid="partners-save-config">Save deed limits</Button>
        </Card>
      )}

      {/* Add Partner dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="rounded-sm sm:max-w-2xl">
          <DialogHeader><DialogTitle>New Partner</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Name</Label>
              <Input value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })}
                     className="rounded-sm mt-1" data-testid="np-name" /></div>
            <div><Label>PAN (10 chars)</Label>
              <Input value={newP.pan} onChange={(e) => setNewP({ ...newP, pan: e.target.value.toUpperCase() })}
                     maxLength={10} className="rounded-sm mt-1 font-mono" data-testid="np-pan" /></div>
            {isLLP && <div><Label>DIN (LLP DPIN)</Label>
              <Input value={newP.din} onChange={(e) => setNewP({ ...newP, din: e.target.value })}
                     className="rounded-sm mt-1 font-mono" data-testid="np-din" /></div>}
            <div className="md:col-span-2"><Label>Address</Label>
              <Input value={newP.address} onChange={(e) => setNewP({ ...newP, address: e.target.value })}
                     className="rounded-sm mt-1" /></div>
            <div><Label>Profit share %</Label>
              <Input type="number" step="0.01" value={newP.profit_share_pct}
                     onChange={(e) => setNewP({ ...newP, profit_share_pct: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" data-testid="np-share" /></div>
            <div><Label>Interest rate %</Label>
              <Input type="number" step="0.5" value={newP.interest_rate_pct}
                     onChange={(e) => setNewP({ ...newP, interest_rate_pct: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>{isLLP ? "Partner type" : "Working / Sleeping"}</Label>
              {isLLP ? (
                <Select value={newP.is_designated_partner ? "designated" : "general"}
                        onValueChange={(v) => setNewP({ ...newP, is_designated_partner: v === "designated" })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="designated">Designated Partner</SelectItem>
                    <SelectItem value="general">General Partner</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Select value={newP.remuneration_slab}
                        onValueChange={(v) => setNewP({ ...newP, remuneration_slab: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="working">Working (remuneration eligible)</SelectItem>
                    <SelectItem value="non_working">Sleeping / Non-working</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div><Label>Opening capital ₹</Label>
              <Input type="number" value={newP.opening_capital}
                     onChange={(e) => setNewP({ ...newP, opening_capital: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Contributions ₹</Label>
              <Input type="number" value={newP.contributions_during_year}
                     onChange={(e) => setNewP({ ...newP, contributions_during_year: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Withdrawals ₹</Label>
              <Input type="number" value={newP.withdrawals_during_year}
                     onChange={(e) => setNewP({ ...newP, withdrawals_during_year: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Interest on capital paid ₹</Label>
              <Input type="number" value={newP.interest_on_capital_paid}
                     onChange={(e) => setNewP({ ...newP, interest_on_capital_paid: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Remuneration paid ₹</Label>
              <Input type="number" value={newP.remuneration_paid}
                     onChange={(e) => setNewP({ ...newP, remuneration_paid: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
            <div><Label>Share of profit ₹</Label>
              <Input type="number" value={newP.share_of_profit_credited}
                     onChange={(e) => setNewP({ ...newP, share_of_profit_credited: Number(e.target.value) })}
                     className="rounded-sm mt-1 font-mono" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button onClick={create} className="bg-accent hover:bg-accent/90 text-white"
                     data-testid="np-save">Save partner</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 40(b) compliance dialog */}
      <Dialog open={complianceOpen} onOpenChange={setComplianceOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="partners-compliance-dialog">
          <DialogHeader><DialogTitle>Sec 40(b) compliance check</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>FY Book profit (before partner remuneration)</Label>
              <Input type="number" value={bookProfit}
                       onChange={(e) => setBookProfit(Number(e.target.value))}
                       className="rounded-sm mt-1 font-mono" data-testid="partners-compliance-bp" />
            </div>
            <Button onClick={runCompliance} size="sm"
                      className="rounded-sm bg-accent hover:bg-accent/90 text-white">
              Run 40(b) check
            </Button>
            {complianceResult && (
              <div className="rounded-sm border p-3 space-y-2 text-xs">
                <div className="flex items-center gap-1 font-medium">
                  <b>Remuneration:</b>
                  {complianceResult.remuneration.within_limit
                    ? <span className="text-emerald-600">✓ Within limit</span>
                    : <span className="text-red-600">✗ Exceeded</span>}
                </div>
                <div>Paid: <b>{fmtINR(complianceResult.remuneration.total_paid)}</b> · Allowed: <b>{fmtINR(complianceResult.remuneration.allowed_40b_v)}</b> · Disallowed: <b className="text-red-600">{fmtINR(complianceResult.remuneration.disallowance)}</b></div>
                <div className="flex items-center gap-1 font-medium pt-2 border-t">
                  <b>Interest on capital:</b>
                  {complianceResult.interest.within_limit
                    ? <span className="text-emerald-600">✓ Within {complianceResult.interest.max_allowed_rate_pct}%</span>
                    : <span className="text-red-600">✗ {complianceResult.interest.flagged_partners.length} partner(s) exceed cap</span>}
                </div>
                {complianceResult.interest.flagged_partners.map((f, i) => (
                  <div key={i} className="text-red-600">• {f.partner}: {f.rate}% &gt; cap {f.max}%</div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profit-JV history timeline (Iter-174) */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="partners-jv-history-dialog">
          <DialogHeader><DialogTitle>Profit JV · audit trail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Every profit-distribution voucher for this firm — posted or
              reversed — newest first. Reversed rows kept for audit.
            </div>
            {jvHistory.length === 0 && (
              <div className="text-center py-8 italic text-muted-foreground text-sm">
                No profit JV has been posted for this firm yet.
              </div>
            )}
            <ol className="relative border-l-2 border-accent/30 ml-2 space-y-4">
              {jvHistory.map((h, i) => {
                const reversed = h.status === "reversed";
                const dot = reversed ? "bg-red-500" : "bg-emerald-500";
                return (
                  <li key={h.id} className="ml-4 pl-2" data-testid={`jv-history-${i}`}>
                    <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-background ${dot}`} />
                    <div className="flex items-center flex-wrap gap-2 text-sm">
                      <b>{h.voucher_number}</b>
                      <span className="text-muted-foreground">·</span>
                      <span>{h.date}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest">
                        FY {h.fy || "—"}
                      </span>
                      {reversed
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-700 uppercase font-mono">Reversed</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-700 uppercase font-mono">Posted</span>}
                    </div>
                    <div className="text-xs mt-1 space-y-0.5">
                      <div>Total credited: <b>{fmtINR(h.total_credited)}</b> · Partners: {h.partners_credited}</div>
                      {h.narration && <div className="text-muted-foreground truncate">{h.narration}</div>}
                      {reversed && h.reversed_at && (
                        <div className="text-red-600 text-[11px]">
                          Reversed on {h.reversed_at.slice(0, 10)}
                          {h.reversal_reason ? ` · ${h.reversal_reason.replace(/_/g, " ")}` : ""}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Profit JV dialog — Admin-defined + confirmation */}
      <Dialog open={jvOpen} onOpenChange={setJvOpen}>
        <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="partners-jv-dialog">
          <DialogHeader><DialogTitle>Post Partner Profit JV · Admin only</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm border-l-4 border-blue-500 bg-blue-500/5 p-3 text-xs leading-relaxed">
              At year-end, distributable profit is debited to <b>Retained Earnings /
              P&amp;L Appropriation</b> and credited to each partner's capital
              account per their profit share. Admin can override individual
              amounts before posting. Voucher is idempotent per (org, FY).
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Distributable profit ₹</Label>
                <Input type="number" value={jvProfit}
                         onChange={(e) => setJvProfit(Number(e.target.value))}
                         className="rounded-sm mt-1 font-mono" data-testid="jv-profit" />
              </div>
              <div>
                <Label>JV date (default 31-Mar)</Label>
                <Input type="date" value={jvDate}
                         onChange={(e) => setJvDate(e.target.value)}
                         className="rounded-sm mt-1" data-testid="jv-date" />
              </div>
              <div className="flex items-end">
                <Button onClick={async () => {
                  if (jvProfit <= 0) return toast.error("Enter distributable profit");
                  setJvBusy(true);
                  try {
                    const r = await api.post("/firm-partners/profit-jv/preview", {
                      distributable_profit: jvProfit, overrides: jvOverrides,
                      date: jvDate || undefined,
                    });
                    setJvPreview(r.data);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Preview failed");
                  }
                  setJvBusy(false);
                }} disabled={jvBusy} size="sm"
                          className="rounded-sm bg-accent hover:bg-accent/90 text-white w-full"
                          data-testid="jv-preview-btn">
                  {jvBusy ? "Computing…" : "Preview"}
                </Button>
              </div>
            </div>

            {jvPreview && (
              <>
                {jvPreview.appropriation_missing && (
                  <div className="rounded-sm border border-red-500 bg-red-500/5 p-2 text-xs text-red-700">
                    ⚠ No Retained Earnings / P&amp;L Appropriation account found. Create
                    one in Chart of Accounts first (equity → retained_earnings).
                  </div>
                )}
                {jvPreview.already_posted && (
                  <div className="rounded-sm border border-amber-500 bg-amber-500/5 p-2 text-xs text-amber-700">
                    ⚠ A profit JV already exists for FY {jvPreview.fy}
                    (voucher <b>{jvPreview.already_posted.voucher_number}</b> ·{" "}
                    {jvPreview.already_posted.date}). Confirming below will reverse it
                    and post afresh.
                  </div>
                )}
                <div className="rounded-sm border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left p-2">Partner</th>
                        <th className="text-left p-2">PAN</th>
                        <th className="text-right p-2">Share%</th>
                        <th className="text-right p-2">Computed ₹</th>
                        <th className="text-right p-2">Admin override ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jvPreview.partner_allocations.map((a) => (
                        <tr key={a.partner_id} className="border-t">
                          <td className="p-2 font-medium">{a.name}</td>
                          <td className="p-2 font-mono">{a.pan}</td>
                          <td className="p-2 text-right tabular-nums">{a.profit_share_pct}%</td>
                          <td className="p-2 text-right tabular-nums text-muted-foreground">
                            {fmtINR(a.computed_share)}
                          </td>
                          <td className="p-2 text-right">
                            <Input type="number"
                                     value={jvOverrides[a.partner_id] ?? a.amount_to_credit}
                                     onChange={(e) => setJvOverrides({
                                        ...jvOverrides,
                                        [a.partner_id]: Number(e.target.value),
                                     })}
                                     className="rounded-sm h-8 w-32 font-mono text-right ml-auto"
                                     data-testid={`jv-override-${a.pan}`} />
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t bg-blue-50 font-semibold">
                        <td colSpan={3} className="p-2 text-right">TOTAL CREDIT</td>
                        <td colSpan={2} className="p-2 text-right tabular-nums">
                          {fmtINR(jvPreview.total_credits)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <label className="flex items-start gap-2 text-sm border-t pt-3">
                  <input type="checkbox" checked={jvConfirm}
                            onChange={(e) => setJvConfirm(e.target.checked)}
                            className="mt-1" data-testid="jv-confirm" />
                  <span>
                    I confirm the above allocation is correct and I want to
                    post this JV
                    {jvPreview.already_posted ? " (this will REVERSE the earlier JV)" : ""}.
                  </span>
                </label>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={() => setJvOpen(false)}>Cancel</Button>
                  <Button disabled={!jvConfirm || jvBusy || jvPreview.appropriation_missing}
                            onClick={async () => {
                              setJvBusy(true);
                              try {
                                const r = await api.post("/firm-partners/profit-jv/post", {
                                  distributable_profit: jvProfit,
                                  overrides: jvOverrides,
                                  date: jvDate || undefined,
                                  confirm: true,
                                  override_existing: !!jvPreview.already_posted,
                                });
                                toast.success(`JV ${r.data.voucher_number} posted · ${r.data.partners_credited} partner(s) credited`);
                                setJvOpen(false);
                                load();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Post failed");
                              }
                              setJvBusy(false);
                            }}
                            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
                            data-testid="jv-post-btn">
                    {jvBusy ? "Posting…" : "Post Profit JV"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ITR-5 Schedule dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>        <DialogContent className="rounded-sm sm:max-w-4xl" data-testid="partners-schedule-dialog">
          <DialogHeader><DialogTitle>ITR-5 · Schedule-Partners · FY {schedule?.fy}</DialogTitle></DialogHeader>
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={downloadCSV}
                     className="rounded-sm gap-1.5" data-testid="partners-schedule-dl">
              <Download className="w-3.5 h-3.5" /> CSV
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-[11px]">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="p-1.5 text-left">#</th>
                  <th className="p-1.5 text-left">Name</th>
                  <th className="p-1.5 text-left">PAN</th>
                  <th className="p-1.5 text-right">Share%</th>
                  <th className="p-1.5 text-right">Opening</th>
                  <th className="p-1.5 text-right">Contrib.</th>
                  <th className="p-1.5 text-right">Withdr.</th>
                  <th className="p-1.5 text-right">Interest</th>
                  <th className="p-1.5 text-right">Remun.</th>
                  <th className="p-1.5 text-right">Profit</th>
                  <th className="p-1.5 text-right">Closing</th>
                </tr>
              </thead>
              <tbody>
                {(schedule?.rows || []).map((r) => (
                  <tr key={r.sr} className="border-t">
                    <td className="p-1.5">{r.sr}</td>
                    <td className="p-1.5 font-medium">{r.name}</td>
                    <td className="p-1.5 font-mono">{r.pan}</td>
                    <td className="p-1.5 text-right">{r.profit_share_pct.toFixed(2)}%</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(r.opening_capital)}</td>
                    <td className="p-1.5 text-right tabular-nums text-emerald-700">{fmtINR(r.contributions)}</td>
                    <td className="p-1.5 text-right tabular-nums text-red-600">{fmtINR(r.withdrawals)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(r.interest_on_capital)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(r.remuneration)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(r.share_of_profit)}</td>
                    <td className="p-1.5 text-right tabular-nums font-medium">{fmtINR(r.closing_capital)}</td>
                  </tr>
                ))}
                {schedule?.totals && (
                  <tr className="border-t bg-blue-50 font-semibold">
                    <td colSpan={4} className="p-1.5 text-right">TOTALS</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.opening_capital)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.contributions)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.withdrawals)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.interest_on_capital)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.remuneration)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.share_of_profit)}</td>
                    <td className="p-1.5 text-right tabular-nums">{fmtINR(schedule.totals.closing_capital)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
